/**
 * @format
 */
import AppPopup from '@/components/popups/app-popup';
import EventBus from '@/scripts/event-bus';
import BoardUtility from '@/scripts/board-utility';
import { LandingPageSectionService } from '@/scripts/services/landing-page/landing-page-section-service';

export const FootersBoardServiceMixin = {
    data() {
        return {
            selectedItems: [],
            deletingItems: [],
        };
    },

    computed: {
        baseUrl() {
            return this.$store.getters.baseUrl;
        },

        accountId() {
            return this.$store.state.context.account.id;
        },
    },

    methods: {
        refreshEvent() {
            EventBus.$emit('footers:update');
        },

        openFooterDesigner(item) {
            this.$router.push(`${this.baseUrl}/LandingPages/Footers/${item.id}`);
        },

        editItem(item) {
            const widget = {
                name: 'footer-widget',
                options: {
                    footerId: item.id,
                    accountId: this.accountId,
                },
            };

            EventBus.$emit('open-widget', widget);
        },

        createFooterFromTemplate(item) {
            const widget = {
                name: 'footer-widget',
                options: {
                    footerId: item.id,
                    accountId: this.accountId,
                    isFromTemplate: true,
                },
            };

            EventBus.$emit('open-widget', widget);
        },

        addToMyFooters(item) {
            LandingPageSectionService.addToMySections(this.accountId, item.id)
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        "You've successfully added the footer to My Footers.",
                    );
                    this.refreshEvent();
                })
                .catch(() => {
                    this.$helpers.showFailNotification('There was an error adding the footer.');
                });
        },

        bulkAutoPublish(items) {
            const ids = items.map(({ id }) => id);

            LandingPageSectionService.bulkAutoPublishLandingPageSections(ids)
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        'Your Auto Publish settings have been updated.',
                    );
                })
                .catch(() => {
                    this.$helpers.showFailNotification(
                        'Your Auto Publish settings failed to update.',
                    );
                })
                .finally(() => {
                    this.selectedItems = [];
                    this.refreshEvent();
                });
        },

        onPublish(item) {
            if (!item.autoPublished.isAutoPublished) {
                const widget = {
                    name: 'footer-publish-widget',
                    options: {
                        footerId: item.id,
                    },
                };

                EventBus.$emit('open-widget', widget);
            } else {
                this.$helpers.showFailNotification('This template is already set to Auto Publish');
            }
        },

        onUnpublish({ id }) {
            LandingPageSectionService.unpublish(id)
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        'Your publish settings have been updated.',
                    );
                    this.refreshEvent();
                })
                .catch(() => {
                    this.$helpers.showFailNotification(
                        'An error occured while saving your footer publish settings.',
                    );
                });
        },

        duplicateItem({ id, footer: { name } }) {
            const widget = {
                name: 'duplicate-widget',
                entityId: id,
                entityName: name,
                serviceName: 'LandingPageSections',
                entityType: 'Footer',
                fieldLabel: 'Choose a name for the new footer',
                fieldPlaceholder: 'Footer Name',
                replaceEntityField: 'Name',
                onSuccess: () => {
                    this.refreshEvent();
                },
            };

            EventBus.$emit('open-widget', widget);
        },

        deleteItem(item) {
            this.deletingItems = [item];

            LandingPageSectionService.delete(item.id)
                .then(() => {
                    this.refreshEvent();
                    this.$helpers.showSuccessNotification(
                        'Your footer has been deleted successfully',
                    );
                })
                .catch((error) => {
                    const errorMessage = error.response.data.Message;

                    if (errorMessage.includes('Section is being used')) {
                        const landingPageName = errorMessage.slice(26);

                        this.$helpers.showFailNotification(
                            `This footer is currently being used in ${landingPageName} and cannot be deleted.`,
                        );
                    } else {
                        this.$helpers.showFailNotification(
                            'An error occured while deleting your footer.',
                        );
                    }
                })
                .finally(() => {
                    this.deletingItems = [];
                });
        },

        deleteItems(items) {
            this.deletingItems = [...items];

            const ids = this.deletingItems.map(({ id }) => id);

            LandingPageSectionService.deleteAll(ids)
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        `${
                            ids.length > 1 ? `${ids.length} footers have` : '1 footer has'
                        } been deleted.`,
                    );
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(error.response.data.Message);
                })
                .finally(() => {
                    this.selectedItems = [];
                    this.deletingItems = [];
                    this.refreshEvent();
                });
        },

        createDeleteFooterModal(item, confirmFunction, contentCopy) {
            return () => {
                const actions = {
                    confirm: {
                        label: 'Delete Footer',
                        action: () => confirmFunction(item),
                    },

                    cancel: {
                        label: 'Cancel',
                    },
                };

                const defaultContent =
                    '<p>All information associated with the footer will be deleted. You can not undo this action.</p>';

                this.$modal.show(
                    AppPopup,
                    {
                        type: 'alert-popup',
                        title: 'Are you sure you want to delete this footer?',
                        content: contentCopy ? contentCopy : defaultContent,
                        actions,
                        confirmText: 'Delete',
                    },
                    {
                        height: this.$helpers.setPopupHeight(),
                        width: this.$helpers.setPopupWidth(),
                    },
                );
            };
        },

        createDeleteMultipleFootersModal(confirmFunction, contentCopy) {
            return () => {
                const actions = {
                    confirm: {
                        label: 'Delete Footers',
                        action: () => confirmFunction(),
                    },

                    cancel: {
                        label: 'Cancel',
                    },
                };

                const defaultContent =
                    '<p>All information associated with these footers will be deleted. You can not undo this action.</p>';

                this.$modal.show(
                    AppPopup,
                    {
                        type: 'text-confirm-popup',
                        title: 'Are you sure?',
                        content: contentCopy ? contentCopy : defaultContent,
                        actions,
                        confirmText: 'Delete',
                    },
                    {
                        height: this.$helpers.setPopupHeight(),
                        width: this.$helpers.setPopupWidth(),
                    },
                );
            };
        },

        asFooterItem(item) {
            let publishedDate;
            const isAgencyAvailable =
                item.Account && item.Account.Agency && item.Account.Agency.Name;

            if (item.PublishedAt) {
                publishedDate = item.PublishedAt;
            } else if (item['SharedAssets@odata.count'] > 0) {
                publishedDate = item.SharedAssets[0].CreatedAt;
            }

            return {
                id: item.Id,
                footer: {
                    id: item.Id,
                    name: item.Name,
                    type: 'footer',
                    link: `Footers/${item.Id}`,
                    previewImageUrl: item.PreviewImageUrl,
                },
                publishedAt: publishedDate ? this.$moment(publishedDate) : null,
                createdAt: this.$moment(item.CreatedAt),
                updatedAt: this.$moment(item.UpdatedAt),
                categories: item.Categories
                    ? item.Categories.map(({ Name }) => ({ name: Name }))
                    : [],
                config: item.Configuration,
                autoPublished: {
                    footerId: item.Id,
                    name: item.Name,
                    isAutoPublished: !!item.IsAutoPublished,
                },
                vendor: isAgencyAvailable
                    ? {
                          logoUrl: item.Account.Agency.LogoImageUrl,
                          name: item.Account.Agency.Name,
                      }
                    : null,
                asset: {
                    footerId: item.Id,
                    isAutoPublished: !!item.IsAutoPublished,
                    published: item['SharedAssets@odata.count'],
                },
                plansPublishedTo: item['SharedAssets@odata.count'],
                landingPagesUsingIt: item.LandingPagesUsing || 0,
            };
        },

        getFilterParam(options, type, clientId, promoId) {
            const queryFilters = [];

            queryFilters.push(
                "Type eq TriDigital.EntityDomain.Entities.LandingPageSectionType'Footer'",
            );

            if (options.filters) {
                const viewQueryFilter = this.getViewQueryFilter(options.filters);
                if (viewQueryFilter) {
                    queryFilters.push(viewQueryFilter);
                }
            }

            if (options.search) {
                queryFilters.push(this.getSearchFilter(options.search));
            }

            if (type === 'footers-shared') {
                queryFilters.push(`SharedAssets/any(a: a/AgencyClient/ClientId eq ${clientId})`);
            } else if (type === 'footers-subscription') {
                queryFilters.push(`SharedAssets/any(a: a/PromoId eq ${promoId})`);
            } else {
                queryFilters.push(`AccountId eq ${this.accountId}`);
            }

            if (type === 'footers-published') {
                queryFilters.push('SharedAssets/any(a: a/AgencyClient/IsAccepted eq true)');
            } else if (type === 'footers-organization-published') {
                queryFilters.push('SharedAssets/any()');
            } else if (
                type === 'footers-unpublished' ||
                type === 'footers-organization-unpublished'
            ) {
                queryFilters.push('not SharedAssets/any()');
            }

            return queryFilters.length > 0 ? `${queryFilters.join(' and ')}` : null;
        },

        getViewQueryFilter(filters) {
            if (filters.value.length > 0) {
                const viewQueryFilters = filters.value.map(this.mapFilter);
                if (filters.operation === 'Or') {
                    return `(${viewQueryFilters.join(' or ')})`;
                } else {
                    return `(${viewQueryFilters.join(' and ')})`;
                }
            }

            return null;
        },

        mapFilter(filter) {
            switch (filter.key) {
                case 'footer-name':
                    return BoardUtility.getStringFilterQuery(filter, 'Name');
                default:
                    return null;
            }
        },

        getSearchFilter(search) {
            const encodedSearch = encodeURIComponent(search);

            return `(contains(Name, '${encodedSearch}'))`;
        },

        getPageParam(options) {
            return options.itemsPerPage && options.page
                ? `${options.itemsPerPage * (options.page - 1)}`
                : null;
        },

        getTopParam(options) {
            return options.itemsPerPage ? `${options.itemsPerPage}` : null;
        },

        getOrderParam(options) {
            return options.sort ? `${options.sort.property} ${options.sort.direction}` : null;
        },

        getExpandParam(type) {
            const expandParams = ['Categories($select=Name)'];
            if (type === 'footers-shared') {
                expandParams.push('Account($select=Id;$expand=Agency($select=Name,LogoImageUrl))');
            } else if (type === 'footers-published' || type === 'footers-organization-published') {
                expandParams.push('SharedAssets($select=CreatedAt;$count=true;$top=1)');
            }

            return expandParams.join(',');
        },

        createParamsFromBoardQuery(options, type, clientId, promoId) {
            return {
                filter: this.getFilterParam(options, type, clientId, promoId),
                skip: this.getPageParam(options),
                top: this.getTopParam(options),
                orderBy: this.getOrderParam(options),
                expand: this.getExpandParam(type),
                count: true,
            };
        },

        dispatchGetFooters({ options = {}, type, clientId, promoId }, signal) {
            const requestConfig = this.createParamsFromBoardQuery(options, type, clientId, promoId);

            return new Promise((resolve, reject) => {
                LandingPageSectionService.getAll(requestConfig, signal)
                    .then(({ data }) => {
                        resolve({
                            items: data.value.map(this.asFooterItem),
                            count: data['@odata.count'],
                        });
                    })
                    .catch(reject);
            });
        },
    },
};
